import { RouteProps } from 'routes/AppRouter'
import { useTranslation } from 'react-i18next'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { navigate } from '@reach/router'
import { ROUTE_PATIENTS } from '../../routes/routes-constants'
import styles from './Editor.module.css'
import { RolesService } from '../../modules/users/services/RolesServices'
import { getUserContainer } from '../../container/user-module'
import {
  IUserService,
  LOGGED_USER_SERVICE_KEY,
  ROLES_SERVICE_KEY,
  USER_SERVICE_KEY,
} from '../../modules/users'
import { Role } from 'modules/users/models/Role'
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Typography,
  TextField,
  Box,
  RadioGroup,
  Radio,
  Select,
  Chip,
  MenuItem,
  Modal,
} from '@mui/material'
import { emptyUserDTO, fromModel, User, UserDTO } from '../../modules/users/models/User'
import { Alert } from '@material-ui/lab'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from '../../common/status/StatusService'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { IPatologyService, PATOLOGY_SERVICE_KEY } from '../../modules/patologies'
import { Query } from '../../common/api/Query'
import { getPatologyContainer } from '../../container/patologies-module'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { InputTextDescription } from '../resource/userProfileStylesMui'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { Subscription } from 'rxjs'
import { CustomModal } from '../../components/modal/CustomModal'
import { PageLayout } from 'pages/layout'

const OTHERS_PATHOLOGY_KEY = 'others'

const roleContainer = getUserContainer()
const roleService = roleContainer.get<RolesService>(ROLES_SERVICE_KEY)
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const patologyService = getPatologyContainer().get<IPatologyService>(PATOLOGY_SERVICE_KEY)
const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

interface EditorProps extends RouteProps {
  id?: string
}

const CalendarIcon = () => <img src={calendarIcon} alt="calendar" className={styles.calendarIcon} />

export function Editor(props: EditorProps) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [, setRoles] = useState<Role[]>([])
  const [patient, setPatient] = useState<UserDTO>(emptyUserDTO())
  const [message] = useState<string>()
  const [userLogged, setUserLogged] = useState<User>()
  const [selectedPatologies, setSelectedPatologies] = useState<string[]>([])
  const [possiblePatologies, setPossiblePatologies] = useState<string[]>([])
  const [translatePatologies, setTranslatePatologies] = useState(new Map<string, string>())
  const [DateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [otherPatologyText, setOtherPatologyText] = useState<string>('')
  // Campos que han fallado la validacion, para poder marcarlos en pantalla.
  const [invalidFields, setInvalidFields] = useState<string[]>([])

  const hasOthersSelected = selectedPatologies.some(
    (p) => translatePatologies.get(p) === OTHERS_PATHOLOGY_KEY || p === t(OTHERS_PATHOLOGY_KEY)
  )

  useEffect(() => {
    if (!loggedUser?.id) return
    const subscription = userService.getByID(loggedUser?.id).subscribe((lu) => {
      setUserLogged(lu)
    })
    return () => subscription.unsubscribe()
  }, [loggedUser?.id, userService])

  useEffect(() => {
    const sub1 = patologyService.getFilteredList(new Query({})).subscribe((res) => {
      const pts: string[] = []
      const trans = new Map<string, string>()
      if (res) {
        for (let i = 0; i < res.count; i++) {
          pts.push(t(res.items[i].name))
          trans.set(t(res.items[i].name), res.items[i].name)
        }
      }
      setTranslatePatologies(trans)
      setPossiblePatologies(pts)
    })

    let sub2: Subscription | null = null
    if (props.id) {
      sub2 = userService.getByID(props.id).subscribe((res) => {
        if (res) {
          const dto = fromModel(res)
          setPatient(dto)
        }
      })
    }

    const rolesAux: Role[] = []
    const sub3 = roleService.getAll().subscribe((res) => {
      res.forEach((e) => {
        if (
          e.name === 'externProfessional' ||
          e.name === 'family/Tutor' ||
          e.name === 'carer/Teacher'
        ) {
          rolesAux.push(e)
        }
      })
      setRoles(rolesAux)
    })

    return () => {
      sub1.unsubscribe()
      sub2?.unsubscribe()
      sub3.unsubscribe()
    }
  }, [props.id, loggedUser?.id, t, userService, patologyService, roleService])

  useEffect(() => {
    if (patient.patologies && patient.patologies.length > 0 && translatePatologies.size > 0) {
      const translatedPats = patient.patologies
        .map((p) => {
          const entry = Array.from(translatePatologies.entries()).find(([_, orig]) => orig === p)
          return entry ? entry[0] : p
        })
        .filter((p) => p !== '')
      setSelectedPatologies(translatedPats)
    }
  }, [patient.patologies, translatePatologies])

  useEffect(() => {
    if (translatePatologies.size === 0) return
    if (!patient.patologies || patient.patologies.length === 0) return
    const othersIsSelected = patient.patologies.includes(OTHERS_PATHOLOGY_KEY)
    if (othersIsSelected && patient.otherPatology) {
      setOtherPatologyText(patient.otherPatology)
    }
  }, [translatePatologies, patient.patologies, patient.otherPatology])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPatient({ ...patient, [e.target.id]: e.target.value })
  }

  const handleAutocompleteChange = (pat: string[]) => {
    const trans: string[] = []
    pat.forEach((element: string) => {
      const original = translatePatologies.get(element)
      if (original) trans.push(original)
    })
    setPatient({ ...patient, patologies: trans })
    const stillHasOthers = pat.some((p) => translatePatologies.get(p) === OTHERS_PATHOLOGY_KEY)
    if (!stillHasOthers) setOtherPatologyText('')
  }

  const handleGender = (e: ChangeEvent<HTMLInputElement>) => {
    setPatient({ ...patient, gender: e.target.value === 'male' ? 1 : 2 })
  }

  const handleBack = () => navigate(ROUTE_PATIENTS)

  // Devuelve los campos que faltan, en el mismo orden en que aparecen en el
  // formulario, para poder marcarlos y saltar al primero. Antes solo devolvia
  // true/false y el guardado se cancelaba sin decir nada: el boton parecia roto.
  const missingFields = (): string[] => {
    const faltan: string[] = []
    if (!patient.firstName) faltan.push('firstName')
    if (!patient.lastName) faltan.push('lastName')
    if (!patient.email) faltan.push('email')
    if (hasOthersSelected && !otherPatologyText.trim()) faltan.push('otherPatologyText')
    if (!patient.gender) faltan.push('gender')
    if (!patient.dob) faltan.push('dob')
    return faltan
  }

  const enError = (campo: string) => invalidFields.includes(campo)

  const handleDateChange = (date: Date | null) => {
    if (date) setPatient({ ...patient, dob: date })
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()

    const faltan = missingFields()
    setInvalidFields(faltan)

    if (faltan.length > 0) {
      statusService.sendStatus({ variant: 'error', message: 'missingRequiredFields' })
      // Llevar la vista al primer campo que falta: el formulario es largo y el
      // que falla suele quedar fuera de pantalla.
      document.getElementById(faltan[0])?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    savePatient()
  }

  const savePatient = () => {
    const newPatient: UserDTO = {
      ...patient,
      language: userLogged?.language ?? patient.language,
      isPatient: true,
      // Al crear, el paciente nace activo. Al editar se respeta el estado que
      // tenga: antes se forzaba a true y editar a alguien desactivado lo
      // reactivaba sin querer.
      isActive: props.id ? patient.isActive : true,
      otherPatology: hasOthersSelected ? otherPatologyText.trim() : '',
    }

    if (props.id) {
      const subscription = userService.update(newPatient).subscribe(() => {
        statusService.sendStatus({ variant: 'success' })
        handleBack()
      })
      return () => subscription.unsubscribe()
    } else {
      const subscription = userService.add(newPatient, navigator.language).subscribe((res) => {
        if (res) {
          statusService.sendStatus({ variant: 'success' })
          handleBack()
        }
      })
      return () => subscription.unsubscribe()
    }
  }

  return (
    <PageLayout>
      <form className={styles.form} id={'editUser'} onSubmit={handleSave}>
        <Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('name')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.firstName}
                fullWidth
                id="firstName"
                error={enError('firstName')}
                helperText={enError('firstName') ? t('requiredField') : ''}
                label={''}
                variant="outlined"
                required={true}
                onChange={(e) => handleChange(e)}
              />
            </Box>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('lastName')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.lastName}
                id="lastName"
                error={enError('lastName')}
                helperText={enError('lastName') ? t('requiredField') : ''}
                label={''}
                variant="outlined"
                required={true}
                onChange={(e) => handleChange(e)}
              />
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('email')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.email}
                id="email"
                error={enError('email')}
                helperText={enError('email') ? t('requiredField') : ''}
                label={''}
                variant="outlined"
                required
                onChange={(e) => handleChange(e)}
              />
            </Box>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('patologies')}</Typography>
              <Autocomplete
                multiple
                id="patologies"
                options={possiblePatologies}
                value={selectedPatologies}
                onChange={(_, v) => {
                  setSelectedPatologies(v)
                  handleAutocompleteChange(v)
                }}
                ChipProps={{ onDelete: undefined }}
                className={styles.autocomplete}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" className={styles.autocompleteInput} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      className={styles.chip}
                      onDelete={() => {
                        const newSelected = selectedPatologies.filter((_, i) => i !== index)
                        setSelectedPatologies(newSelected)
                        handleAutocompleteChange(newSelected)
                      }}
                    />
                  ))
                }
                noOptionsText={t('noElementsAvailable')}
                ListboxProps={{ style: { maxHeight: '250px' } }}
              />
              {hasOthersSelected && (
                <Box mt={1}>
                  <Input
                    sx={{ width: '100%' }}
                    id="otherPatologyText"
                    error={enError('otherPatologyText')}
                    helperText={enError('otherPatologyText') ? t('requiredField') : ''}
                    label={''}
                    placeholder={t('otherPathologyPlaceholder')}
                    variant="outlined"
                    value={otherPatologyText}
                    required={hasOthersSelected}
                    onChange={(e) => setOtherPatologyText(e.target.value)}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Campos de la ficha de perfil. Sin ellos aqui no habria forma de
              rellenarlos, porque la ficha es de solo lectura. */}
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('phone')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.phone ?? ''}
                id="phone"
                label={''}
                variant="outlined"
                onChange={(e) => handleChange(e)}
              />
            </Box>
            <Box className={styles.box} display={'flex'} gap={2}>
              <Box sx={{ flex: 1 }}>
                <Typography className={styles.inputTitle}>{t('city')}</Typography>
                <Input
                  sx={{ width: '100%' }}
                  value={patient.city ?? ''}
                  id="city"
                  label={''}
                  variant="outlined"
                  onChange={(e) => handleChange(e)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography className={styles.inputTitle}>{t('country')}</Typography>
                <Input
                  sx={{ width: '100%' }}
                  value={patient.country ?? ''}
                  id="country"
                  label={''}
                  variant="outlined"
                  onChange={(e) => handleChange(e)}
                />
              </Box>
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('sensitiveData')}</Typography>
              <Select
                sx={{ width: '100%' }}
                id="sensitive"
                value={patient.sensitive ? 'sensitive' : 'normal'}
                onChange={(ev) =>
                  setPatient({ ...patient, sensitive: ev.target.value === 'sensitive' })
                }
                variant="outlined"
                size="small"
              >
                <MenuItem value={'normal'}>{t('normal')}</MenuItem>
                <MenuItem value={'sensitive'}>{t('sensitive')}</MenuItem>
              </Select>
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <FormControl sx={{ width: '100%' }}>
                <FormLabel className={styles.inputTitle} id="gender">
                  {t('gender')}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="gender"
                  name="gender"
                  value={patient.gender === 1 ? 'male' : 'female'}
                  onChange={(e) => handleGender(e)}
                >
                  <FormControlLabel
                    value="male"
                    control={<Radio />}
                    className={styles.radioGroup}
                    label={t('male')}
                  />
                  <FormControlLabel
                    value="female"
                    control={<Radio />}
                    className={styles.radioGroup}
                    label={t('female')}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('birthDate')}</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                <DatePicker
                  value={patient.dob}
                  inputFormat={'DD/MM/YYYY'}
                  onChange={handleDateChange}
                  label={''}
                  onError={(reason) => {
                    if (reason === 'invalidDate') setDateTimePickerError(t('invalidDateMessage'))
                    if (reason === 'minDate') setDateTimePickerError(t('minDateMessage'))
                  }}
                  renderInput={(params) => (
                    <TextField
                      sx={{ width: '100%' }}
                      id="dob"
                      size={'small'}
                      required
                      {...params}
                      error={params.error || enError('dob')}
                      helperText={
                        enError('dob') ? t('requiredField') : params.error && DateTimePickerError
                      }
                    />
                  )}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'center'}
              alignItems={'center'}
              className={styles.box}
            >
              <Box className={styles.buttons} mb={3} display="flex" justifyContent={'center'}>
                <Button className={styles.buttonCancel} type={'button'} onClick={handleBack}>
                  {t('back')}
                </Button>
                <Button className={styles.buttonAdd} type={'submit'}>
                  {props.id ? t('editIcon') : t('add')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </form>

      {message && (
        <Box mb={3}>
          <Alert className={styles.message} severity="success" key="message" id="message">
            {t(message)}
          </Alert>
        </Box>
      )}
    </PageLayout>
  )
}
